import { Component, Input } from '@angular/core';
import { IPrintOptions } from '@modeso/types__aumico-lib-annualreport-be';
import { AumicoStyleOptions, ICellFormat } from '@modeso/types__aumico-lib-core-be';
import { SuperTableColumnBelongingYear } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/super_table_column_belonging_year.enum';
import { RowTypes } from '../../../../../aumico-lib-annualreport-fe/src/lib';
import { FIRST_ELEMENT_INDEX } from '../../../../../aumico-lib-core-fe/src/lib';
import { SuperTableHelper } from '../../helpers/super-table.helper';
import { INotesSuperTableColumnForPrintPreview } from '../../interfaces/notes-super-table-column-for-print-preview.interface';
import { INotesSuperTableDataForPrintPreview } from '../../interfaces/notes-super-table-data-for-print-preview.interface';
import { INotesSuperTableRowForPrintPreview } from '../../interfaces/notes-super-table-row-for-print-preview.interface';
import { INotesSuperTableSegmentForPrintPreview } from '../../interfaces/notes-super-table-segment-for-print-preview.interface';

const BLACK_TEXT_COLOR = '#38323C';
const BLUE_HIGHLIGHT_COLOR = '#ecf4fa';
const WHITE = '#FFFFFF';

export enum TransposedSuperTableColumnType {
    EMPTY = 'EMPTY',
    NUMBER = 'NUMBER',
    TEXT = 'TEXT',
    CALCULATION = 'CALC',
}

export interface TransposedINotesSuperTableColumnForPrintPreview {
    columnId: string;
    columnTitle: string;
    type: TransposedSuperTableColumnType;
    belongingYear?: SuperTableColumnBelongingYear;
    isRatio?: boolean;
    format?: ICellFormat;
}

/** The title column of a SuperTable — the template renders it by this id. */
const TITLE_COLUMN_ID = 't';
/** The value-column count the KENDRIS print widths are tuned for (REV-21512). */
const TUNED_VALUE_COLUMN_COUNT = 4;

@Component({
    selector: 'aumico-notes-super-table-display',
    templateUrl: './notes-super-table-display.component.html',
    styleUrls: ['./notes-super-table-display.component.scss'],
})
export class NotesSuperTableDisplayComponent {
    TransposedSuperTableColumnType = TransposedSuperTableColumnType;
    textColor = BLACK_TEXT_COLOR;
    backgroundColor = BLUE_HIGHLIGHT_COLOR;
    backgroundColorPY = WHITE;
    tableFooterColor: string;
    tableHeaderColor: string;
    superTableRows: INotesSuperTableRowForPrintPreview[] = [];
    transposedRows: INotesSuperTableRowForPrintPreview[] = [];
    transposedColumns: TransposedINotesSuperTableColumnForPrintPreview[] = [];
    superTableColumns: INotesSuperTableColumnForPrintPreview[] = [];
    transpose: boolean = false;
    hasSegments: boolean = false;
    tableTitle: string = 'false';
    componentTitle: string | undefined;
    private superTableSegments: INotesSuperTableSegmentForPrintPreview[] = [];
    allSegmentsChildColumnIds: string[] = [];
    startingColumnIdToSegment: Map<string, INotesSuperTableSegmentForPrintPreview> = new Map<
        string,
        INotesSuperTableSegmentForPrintPreview
    >();
    lastColumnIdInSegments: string[] = [];
    public highlightedSegmentMap = new Map<string, boolean>();
    public highlightedSegmentMapPY = new Map<string, boolean>();
    footnotes: string[];
    spacerColumns: string[] = []; // Column IDs after which to insert spacers
    /**
     * REV-21512: spacers that sit BETWEEN two columns of the SAME year block carry that block's
     * highlight — otherwise a white gap cuts through it, e.g. between the functional and the
     * display-currency column of the current year. Keyed by the column the spacer follows.
     */
    spacerYearBlock = new Map<string, 'CY' | 'PV'>();
    showSpacerBeforeCalculationRows = false;
    /**
     * REV-21512: true when exactly four value columns print (e.g. an FX table showing both
     * currencies for both years). The Word export needs it as a table class: the docx HTML
     * module treats declared widths as shares and starts a width-less column at
     * `tableWidth / columnCount`, so more columns shrink every column. The KENDRIS stylesheet
     * carries width values tuned for this count.
     */
    hasFourValueColumns = false;

    @Input() set sectionNotes(value: INotesSuperTableDataForPrintPreview | undefined | string) {
        if (value == null || value === '') {
            return;
        }
        this.footnotes = (value as INotesSuperTableDataForPrintPreview).footnotes || [];
        this.superTableRows = (value as INotesSuperTableDataForPrintPreview).rows;
        this.superTableColumns = (value as INotesSuperTableDataForPrintPreview).columns;
        this.superTableSegments = (value as INotesSuperTableDataForPrintPreview).segments;
        this.transpose = (value as INotesSuperTableDataForPrintPreview).transpose;
        this.componentTitle = (value as INotesSuperTableDataForPrintPreview).componentTitle;
        this.spacerColumns = (value as INotesSuperTableDataForPrintPreview).spacerColumns || [];
        this.showSpacerBeforeCalculationRows =
            (value as INotesSuperTableDataForPrintPreview).showSpacerBeforeCalculationRows || false;
        this.hasFourValueColumns =
            this.superTableColumns.filter((column) => column.columnId !== TITLE_COLUMN_ID).length ===
            TUNED_VALUE_COLUMN_COUNT;
        this.prepareSpacerYearBlocks();
        this.prepareStartingColumnIdToSegmentMap();
    }

    @Input() set printOptionStyles(value: IPrintOptions | undefined) {
        if (value != null) {
            this.textColor = value.styleOptions.textColor;
            this.backgroundColor = value.styleOptions.backgroundColor;
            this.backgroundColorPY = value.styleOptions.backgroundColorPY;
            this.tableHeaderColor = value.styleOptions.tableHeaderColor;
            this.tableFooterColor = value.styleOptions.tableFooterColor;
        }
    }
    @Input() colored: boolean = false;

    @Input() highlightTodos = false;

    @Input() tableNumber: string | undefined;

    constructor(private readonly superTableHelper: SuperTableHelper) { }

    /** The year block a column is painted with — mirrors the header's highlight branches. */
    private yearBlockOf(column: INotesSuperTableColumnForPrintPreview): 'CY' | 'PV' | undefined {
        if (column.type === 'CY' || column.belongingYear === 'CY') {
            return 'CY';
        }
        if (column.type === 'PV' || column.belongingYear === 'PV') {
            return 'PV';
        }
        return undefined;
    }

    private prepareSpacerYearBlocks(): void {
        this.spacerYearBlock.clear();
        for (let i = 0; i < this.superTableColumns.length - 1; i++) {
            const block = this.yearBlockOf(this.superTableColumns[i]);
            if (block != null && block === this.yearBlockOf(this.superTableColumns[i + 1])) {
                this.spacerYearBlock.set(this.superTableColumns[i].columnId, block);
            }
        }
    }

    /** Background of a spacer that continues a highlighted block; empty for a plain gap. */
    spacerBackgroundColor(columnId: string): string {
        const block = this.spacerYearBlock.get(columnId);
        if (block === 'CY') {
            return this.backgroundColor;
        }
        return block === 'PV' ? this.backgroundColorPY : '';
    }

    private prepareStartingColumnIdToSegmentMap(): void {
        for (const segment of this.superTableSegments) {
            this.startingColumnIdToSegment.set(segment.childColumnsIds[FIRST_ELEMENT_INDEX], segment);
            this.allSegmentsChildColumnIds.push(...segment.childColumnsIds);
            this.lastColumnIdInSegments.push(
                segment.childColumnsIds[segment.childColumnsIds.length - 1]
            );
        }
        this.hasSegments = this.superTableSegments.length > 0;

        if (this.transpose && this.superTableColumns.length > 0) {
            const [firstColumn, ...remainingColumns] = this.superTableColumns;
            if (firstColumn) {
                this.tableTitle = firstColumn.columnTitle;
                this.superTableColumns = remainingColumns;
                this.transposedRows = this.transposeRows();
                this.transposedColumns = this.transposeColumns();
            }
        }
        this.calculateHighlightedSegmentMap();
        this.calculateHighlightedSegmentMapPY();
    }

    private calculateHighlightedSegmentMap(): void {
        this.highlightedSegmentMap.clear();
        for (const segment of this.superTableSegments) {
            if (segment && segment.childColumnsIds && segment.childColumnsIds.length > 0) {
                const shouldHighlight = segment.childColumnsIds.every(childId => {
                    const column = this.superTableColumns.find(c => c.columnId === childId);
                    return column && column.belongingYear === SuperTableColumnBelongingYear.CURRENT_YEAR;
                });
                this.highlightedSegmentMap.set(segment.childColumnsIds[0], shouldHighlight);
            }
        }
    }

    private calculateHighlightedSegmentMapPY(): void {
        this.highlightedSegmentMapPY.clear();
        for (const segment of this.superTableSegments) {
            if (segment && segment.childColumnsIds && segment.childColumnsIds.length > 0) {
                const shouldHighlight = segment.childColumnsIds.every(childId => {
                    const column = this.superTableColumns.find(c => c.columnId === childId);
                    return column && column.belongingYear === SuperTableColumnBelongingYear.PREVIOUS_YEAR;
                });
                this.highlightedSegmentMapPY.set(segment.childColumnsIds[0], shouldHighlight);
            }
        }
    }

    transposeRows(): INotesSuperTableRowForPrintPreview[] {
        const transposedRows: INotesSuperTableRowForPrintPreview[] = [];
        const maxColumns = this.superTableColumns.length;
        const maxRowNumber = this.superTableRows.length;

        for (let i = 0; i < maxColumns; i++) {
            let rowType = RowTypes.ROW;
            if (this.superTableColumns[i].isRatio) {
                rowType = RowTypes.CALC_ROW;
            }
            const transposedRow: INotesSuperTableRowForPrintPreview = {
                rowType: rowType,
                t: this.superTableColumns[i].columnTitle,
                format: this.superTableColumns[i].format,
            };

            for (let j = 0; j < maxRowNumber; j++) {
                const columnId = this.superTableColumns[i].columnId;
                const row = this.superTableRows[j];
                if (this.superTableColumns[i].isRatio) {
                    const value = row[columnId];
                    transposedRow['row' + j.toString()] = value != null ? value : null;
                } else {
                    transposedRow['row' + j.toString()] = row[columnId];
                }
            }
            transposedRows.push(transposedRow);
        }

        return transposedRows;
    }

    transposeColumns(): TransposedINotesSuperTableColumnForPrintPreview[] {
        const transposedCols: TransposedINotesSuperTableColumnForPrintPreview[] = [];
        const maxRowNumber = this.superTableRows.length;

        for (let i = 0; i < maxRowNumber; i++) {
            const rowType = this.superTableRows[i].rowType;
            let type = TransposedSuperTableColumnType.NUMBER;

            if (rowType === RowTypes.EMPTY_ROW) {
                type = TransposedSuperTableColumnType.EMPTY;
            }
            const transposedCol: TransposedINotesSuperTableColumnForPrintPreview = {
                columnId: 'row' + i.toString(),
                columnTitle: '',
                type,
                isRatio: false,
                format: this.superTableRows[i].format,
            };

            transposedCols.push(transposedCol);
        }

        return transposedCols;
    }

    resolveEffectiveCellStyles(rowFormat?: ICellFormat, colFormat?: ICellFormat): AumicoStyleOptions[] {
        return this.superTableHelper.resolveEffectiveCellStyles(rowFormat, colFormat);
    }

    resolveEffectiveDecimalPlaces(rowFormat?: ICellFormat, colFormat?: ICellFormat): number | undefined {
        return this.superTableHelper.resolveEffectiveDecimalPlaces(rowFormat, colFormat);
    }

    // Calculate the total pixel width for a segment by summing the widths of its child columns.
    public getLengthSegment(segment?: INotesSuperTableSegmentForPrintPreview | null): number {
        if (!segment || !Array.isArray(segment.childColumnsIds) || segment.childColumnsIds.length === 0) {
            return 0;
        }

        let total = 0;
        for (const childId of segment.childColumnsIds) {
            const col = this.superTableColumns.find((c) => c.columnId === childId);
            // If column not found, assume standard width
            const widthKey = col?.columnWidth ?? 'standard';
            switch (widthKey) {
                case 'small':
                    total += 60;
                    break;
                case 'large':
                    total += 240;
                    break;
                case 'dynamic':
                    total += 120;
                    break;
                case 'standard':
                default:
                    total += 120;
            }
        }

        return total;
    }

    /**
     * Calculate colspan for a segment including all spacers within the segment
     */
    public getSegmentColspan(segment?: INotesSuperTableSegmentForPrintPreview | null): number {
        if (!segment || !Array.isArray(segment.childColumnsIds) || segment.childColumnsIds.length === 0) {
            return 0;
        }

        let colspan = segment.childColumnsIds.length;

        // Count only spacers that are completely inside the segment's borders
        // (between child columns, not after the last one)
        for (let i = 0; i < segment.childColumnsIds.length - 1; i++) {
            const childId = segment.childColumnsIds[i];
            if (this.shouldShowSpacerAfter(childId)) {
                colspan++;
            }
        }

        return colspan;
    }

    /**
     * Check if a spacer column should be displayed after the given column
     * This was once built for a custom-client requirement (KENDRIS) which need spacer columns
     */
    shouldShowSpacerAfter(columnId: string): boolean {
        return this.spacerColumns.includes(columnId);
    }

    shouldShowSpacerAfterInSegment(columnId: string): boolean {
        if (!this.lastColumnIdInSegments.includes(columnId)) {
            return false;
        }
        return this.shouldShowSpacerAfter(columnId);
    }

    shouldShowSpacerBeforeCalculationRow(row: INotesSuperTableRowForPrintPreview): boolean {
        return !this.transpose && this.showSpacerBeforeCalculationRows && row.rowType === RowTypes.CALC_ROW;
    }
}
